/**
 * Sign in.
 *
 * The one screen that renders without a session, so it must not depend on anything the guarded
 * API provides — no reference data, no bootstrap, no config. Everything it shows is either static
 * or comes back from `/auth/login` itself.
 */

import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { LoginError, login } from '../api/auth_api'
import { useAuth } from '../contexts/auth_context'

export function LoginPage() {
  const { session, checking, refresh } = useAuth()
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  //  ?session_expired=1 is set by the global guard. Saying so explicitly is the difference
  //  between "why am I back at the login page?" and a login screen that explains itself.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('session_expired')) {
      setNotice('Your session ended. Please sign in again.')
    }
  }, [])

  //  Already signed in — do not show a login form to someone who is. Covers the back button and a
  //  second tab where the session is still live.
  useEffect(() => {
    if (!checking && session.authenticated) navigate('/home', { replace: true })
  }, [checking, session.authenticated, navigate])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    setErrorCode(null)
    setNotice(null)
    try {
      await login(username, password)
      await refresh()
      navigate('/home', { replace: true })
    } catch (err) {
      if (err instanceof LoginError) {
        setError(err.message)
        setErrorCode(err.errorCode)
      } else {
        setError('Sign-in failed. Please try again.')
      }
      //  Clear only the password. Retyping a correct username after a typo in the password is a
      //  small, avoidable annoyance.
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="tf-login">
      <div className="tf-login__wave" aria-hidden="true" />

      <form className="tf-login__card" onSubmit={submit}>
        <div className="tf-login__brand">
          <img src="/img/tidalfin-mark.svg" alt="" width={44} height={44} />
          <div>
            <h1>Tidalfin</h1>
            <p>Relationships &amp; Campaigns</p>
          </div>
        </div>

        {notice && (
          <div className="tf-login__notice" role="status">
            {notice}
          </div>
        )}

        {error && (
          <div className="tf-login__error" role="alert">
            <span className="material-symbols-rounded" aria-hidden="true">
              error
            </span>
            <div>
              <strong>{error}</strong>
              {/* A refused tenant is not a bad password, and telling someone to retype their
                  password when the password was right sends them to reset it for nothing. */}
              {errorCode === 'NOT_A_TENANT_MEMBER' && (
                <p>Your sign-in was recognised — the account simply has no access to this workspace.</p>
              )}
              {(errorCode === 'IDENTITY_UNREACHABLE' ||
                errorCode === 'MEMBERSHIP_LOOKUP_FAILED' ||
                errorCode === 'IDENTITY_INTERNAL_ERROR') && (
                <p>This is a service problem, not your credentials. Trying again shortly usually works.</p>
              )}
            </div>
          </div>
        )}

        <label className="tf-login__field">
          <span>Username</span>
          <input
            type="text"
            name="username"
            autoComplete="username"
            autoFocus
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={busy}
          />
        </label>

        <label className="tf-login__field">
          <span>Password</span>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
        </label>

        <button type="submit" className="tf-login__submit" disabled={busy || !username || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="tf-login__foot">
          Tidalfin uses your BlueHomer account. Access is granted per workspace — if you cannot get
          in, ask an administrator rather than resetting your password.
        </p>
      </form>
    </div>
  )
}
